import React, { useContext } from "react"
import { View, StyleSheet, Image, TouchableOpacity } from "react-native"
import Animated, {
    Extrapolation,
    interpolate,
    useAnimatedScrollHandler,
    useAnimatedStyle,
    useSharedValue,
} from "react-native-reanimated"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import logo from "@assets/logo-blue.png"
import BrevetFunnelHeader from "../../../brevet/components/BrevetFunnelHeader"
import AnswerCardM from "./AnswerCardM"
import ClassChipGridM from "./ClassChipGridM"

// Taille de titre PARTAGÉE par tout le funnel (écrans illustrés ET non
// illustrés). Exportée pour que le configurateur / checkout calent EXACTEMENT le
// même rendu typographique (homogénéité demandée : seule l'illustration diffère,
// pas l'échelle typo). 24/30 Montserrat-ExtraBold.
export const FUNNEL_TITLE_FONT_SIZE = 24
export const FUNNEL_TITLE_LINE_HEIGHT = 30

/**
 * Template d'écran de question funnel — refonte "Preply-grade".
 *
 * DEUX rendus selon la largeur de la fenêtre (responsive, breakpoint ~900px) :
 *
 *  - ÉTROIT (mobile) : bandeau pastel ÉPINGLÉ tout en haut, avec l'illustration
 *    en COUCHE DE FOND À L'INTÉRIEUR. Au scroll vers le bas : la HAUTEUR du
 *    bandeau se réduit ET l'illustration REMONTE (translateY négatif, parallaxe
 *    plus rapide que le scroll), donc elle glisse vers le haut et se fait CLIPPER
 *    par le bord supérieur du bandeau (overflow:hidden) → elle disparaît
 *    progressivement derrière le haut. Le bandeau ne s'efface PAS complètement
 *    (il rétrécit jusqu'à BAND_MIN, il reste collé en haut). La FEUILLE DE
 *    CONTENU (titre + helper + cartes) scrolle NORMALEMENT vers le haut et
 *    PAR-DESSUS le bandeau — le TITRE fait partie du contenu qui défile, calé
 *    juste sous le bandeau, et n'est PLUS épinglé par-dessus l'image. CTA collant
 *    en bas. (Comportement onboarding Preply : bande pastel en haut, l'image
 *    remonte hors champ au scroll tandis que la question + les options défilent.)
 *
 *  - LARGE (desktop, split type Preply) : deux colonnes — à GAUCHE un panneau
 *    pleine hauteur en pastel avec l'illustration centrée, le logo Masteur en bas
 *    et la flèche retour en haut ; à DROITE un panneau blanc avec la question, les
 *    cartes et le CTA (colonne de contenu max ~480px). Le panneau gauche est fixe
 *    (pas de collapse-on-scroll en large).
 *
 * Interaction clé (étroit) : PARALLAXE pilotée par le scroll. La hauteur du
 * bandeau et le translateY de l'image sont des fonctions PURES de scrollY → le
 * retour au scroll-up est automatique, sans état JS ni listener manuel.
 * Implémenté via Reanimated 3 (useSharedValue + useAnimatedScrollHandler +
 * interpolate), piloté côté UI thread en natif et compatible Expo web.
 *
 * Générique : aucune logique métier funnel ici. L'appelant fournit la question,
 * les options, l'état sélectionné et le handler. Réutilisable été / brevet /
 * essai (les deux funnels partagent déjà BrevetFunnelHeader / BrevetChip).
 *
 * Props :
 *  - illustration            : source d'image (require(...)) pour le bandeau.
 *  - illustrationBgColor     : couleur de fond du bandeau (défaut primaryLighter).
 *  - question                : string — la question (grande, en gras).
 *  - helper                  : string optionnel — précision sous la question.
 *  - options                 : [{ id, label, subLabel?, emoji?, iconUri? }].
 *  - selectedIds             : string[] — ids sélectionnés (mono = longueur 0/1).
 *  - onToggle(id)            : appelé au tap d'une option.
 *  - chipGrid                : bool — rend les options en grille de pastilles
 *                              compactes (ClassChipGridM) au lieu de cartes pleine
 *                              largeur. Pour les listes longues (ex. classes).
 *  - multi                   : bool — multi-sélection (checkbox) sinon radio.
 *  - maxSelections           : number optionnel — en multi, plafonne la sélection :
 *                              au plafond, les cartes non cochées sont désactivées.
 *  - ctaLabel                : libellé du bouton (défaut "Continuer").
 *  - onContinue()            : action du CTA.
 *  - ctaLoading              : bool — spinner sur le CTA.
 *  - canContinue             : bool optionnel — force l'état actif du CTA
 *                              (défaut : au moins une option sélectionnée).
 *  - step / total / onBack   : passés à l'en-tête (barre de progression).
 *  - hideBack                : masque le bouton retour.
 *  - HeaderComponent         : en-tête de progression. Le funnel stage a le
 *                              sien (segments, sans compteur) ; les autres
 *                              gardent celui du brevet.
 */

// Bandeau pastel (étroit) : hauteur au repos → hauteur repliée (NON nulle : la
// bande reste collée en haut, elle rétrécit mais ne disparaît jamais ; c'est
// l'IMAGE qui part vers le haut hors champ). Exportées pour que les écrans
// bespoke (ex. SummerClassScreen) calent EXACTEMENT le même parallaxe.
export const BAND_MAX = 200 // hauteur du bandeau au repos
export const BAND_MIN = 56 // hauteur repliée résiduelle (la bande reste visible)
export const BAND_COLLAPSE_RANGE = BAND_MAX - BAND_MIN
// Amplitude du parallaxe : l'image remonte de BAND_PARALLAX_RISE px sur la
// course → nettement plus que la réduction de hauteur, donc elle se fait clipper
// par le haut du bandeau et recule hors champ.
export const BAND_PARALLAX_RISE = Math.round(BAND_MAX * 0.95)

// Breakpoint split desktop. Au-delà, on passe en deux colonnes (panneau pastel
// + colonne de contenu). En-deçà, layout mobile empilé.
export const WIDE_BREAKPOINT = 900
// Largeur max de la colonne de contenu (question + cartes + CTA) en large.
const WIDE_CONTENT_MAX = 480

const FunnelQuestionScreen = ({
    illustration,
    illustrationBgColor = theme.colors.primaryLighter,
    question,
    helper,
    options = [],
    selectedIds = [],
    onToggle,
    chipGrid = false,
    multi = false,
    maxSelections,
    ctaLabel = "Continuer",
    onContinue,
    ctaLoading = false,
    canContinue,
    step,
    total,
    onBack,
    hideBack = false,
    HeaderComponent = BrevetFunnelHeader,
}) => {
    const dimensions = useContext(DimensionsContext)
    const windowWidth = dimensions?.width || 400
    const isWide = windowWidth >= WIDE_BREAKPOINT

    // Alias locaux (les constantes exportées portent le préfixe BAND_).
    const COLLAPSE_RANGE = BAND_COLLAPSE_RANGE
    const PARALLAX_RISE = BAND_PARALLAX_RISE

    const scrollY = useSharedValue(0)
    const scrollHandler = useAnimatedScrollHandler({
        onScroll: (event) => {
            scrollY.value = event.contentOffset.y
        },
    })

    // Bandeau pastel : hauteur qui se réduit avec le scroll, MAIS reste visible
    // (clamp à BAND_MIN, jamais 0). La bande reste collée en haut.
    const bandAnimatedStyle = useAnimatedStyle(() => {
        const height = interpolate(
            scrollY.value,
            [0, COLLAPSE_RANGE],
            [BAND_MAX, BAND_MIN],
            Extrapolation.CLAMP
        )
        return { height }
    })

    // Illustration : REMONTE (translateY négatif) plus vite que le scroll →
    // parallaxe ; le bord supérieur du bandeau (overflow:hidden) la clippe, donc
    // elle disparaît vers le haut. Léger fade pour qu'elle s'efface proprement
    // une fois bien remontée.
    const imageAnimatedStyle = useAnimatedStyle(() => {
        const translateY = interpolate(
            scrollY.value,
            [0, COLLAPSE_RANGE],
            [0, -PARALLAX_RISE],
            Extrapolation.CLAMP
        )
        const opacity = interpolate(
            scrollY.value,
            [0, COLLAPSE_RANGE * 0.85],
            [1, 0],
            Extrapolation.CLAMP
        )
        return { transform: [{ translateY }], opacity }
    })

    const isActive =
        canContinue !== undefined ? canContinue : selectedIds.length > 0

    // Bloc TITRE seul (question + helper) — épinglé en narrow, en tête de colonne
    // en wide. Séparé des réponses pour que le titre reste visible au scroll.
    const titleBlock = (
        <>
            <TextM wrap style={styles.question}>
                {question}
            </TextM>
            {helper ? (
                <TextM wrap style={styles.helper}>
                    {helper}
                </TextM>
            ) : null}
        </>
    )

    // Bloc RÉPONSES — soit une grille de pastilles compactes (chipGrid, listes
    // longues), soit la liste de cartes pleine largeur (défaut).
    const answersBlock = chipGrid ? (
        <ClassChipGridM
            options={options}
            selectedIds={selectedIds}
            onToggle={onToggle}
            style={styles.chipGrid}
        />
    ) : (
        <View style={styles.answersList}>
            {options.map((opt) => {
                const isSelected = selectedIds.includes(opt.id)
                // En multi-sélection plafonnée : au plafond atteint, les cartes
                // non cochées sont désactivées (on reste libre de décocher une
                // carte déjà choisie).
                const cappedOut =
                    multi &&
                    maxSelections != null &&
                    !isSelected &&
                    selectedIds.length >= maxSelections
                return (
                    <AnswerCardM
                        key={opt.id}
                        label={opt.label}
                        subLabel={opt.subLabel}
                        emoji={opt.emoji}
                        iconUri={opt.iconUri}
                        multi={multi}
                        selected={isSelected}
                        disabled={cappedOut}
                        onPress={() => onToggle?.(opt.id)}
                    />
                )
            })}
        </View>
    )

    const ctaFooter = (
        <View style={styles.footer}>
            <FilledButtonM
                label={ctaLabel}
                onPress={onContinue}
                disabled={!isActive}
                loading={ctaLoading}
                color={isActive ? theme.colors.primary : theme.colors.lightMore}
                style={{ width: "100%", marginBottom: 0 }}
            />
        </View>
    )

    // ===== LARGE (desktop) : split deux colonnes, panneau gauche fixe =====
    if (isWide) {
        return (
            <View style={styles.wideRoot}>
                {/* Panneau pastel gauche (illustration + logo + retour) */}
                <View
                    style={[
                        styles.widePanel,
                        { backgroundColor: illustrationBgColor },
                    ]}
                >
                    {!hideBack ? (
                        <TouchableOpacity
                            onPress={onBack}
                            style={styles.wideBack}
                            hitSlop={{
                                top: 12,
                                bottom: 12,
                                left: 12,
                                right: 12,
                            }}
                        >
                            <FontAwesome5
                                name="chevron-left"
                                size={18}
                                color={theme.colors.primary}
                            />
                        </TouchableOpacity>
                    ) : null}
                    {illustration ? (
                        <Image
                            source={illustration}
                            style={styles.wideIllustration}
                            resizeMode="contain"
                        />
                    ) : null}
                    <Image
                        source={logo}
                        style={styles.wideLogo}
                        resizeMode="contain"
                    />
                </View>

                {/* Colonne blanche droite (progress + question + cartes + CTA) */}
                <View style={styles.wideContentCol}>
                    <View style={styles.wideContentInner}>
                        <HeaderComponent
                            step={step}
                            total={total}
                            hideBack
                            hideLogo
                        />
                        <Animated.ScrollView
                            contentContainerStyle={styles.wideScrollContent}
                            showsVerticalScrollIndicator={false}
                        >
                            {titleBlock}
                            {answersBlock}
                        </Animated.ScrollView>
                        {ctaFooter}
                    </View>
                </View>
            </View>
        )
    }

    // ===== ÉTROIT (mobile) : bandeau pastel épinglé en haut + parallaxe image =====
    // Architecture (Preply onboarding) :
    //   - Progression épinglée tout en haut (petite barre opaque).
    //   - BANDEAU pastel pinné juste sous la progression (absolute, overflow:hidden) :
    //     l'illustration vit DEDANS, en fond. Au scroll, la hauteur du bandeau
    //     rétrécit (jusqu'à BAND_MIN, jamais 0) ET l'image REMONTE (parallaxe) →
    //     elle se fait clipper par le haut du bandeau et disparaît vers le haut.
    //   - ScrollView par-dessus, AVEC le TITRE en tête du contenu (plus épinglé) :
    //     un spacer transparent de BAND_MAX réserve la place du bandeau au repos ;
    //     en scrollant, le titre + les cartes remontent par-dessus le bandeau et
    //     le titre se cale juste sous le bord inférieur du bandeau rétréci.
    const containerWidth = Math.min(windowWidth, 460)
    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                {/* Progression épinglée tout en haut (au-dessus du bandeau). */}
                <View style={styles.pinnedHeader}>
                    <HeaderComponent
                        step={step}
                        total={total}
                        onBack={onBack}
                        hideBack={hideBack}
                    />
                </View>

                {/* Scène : bandeau pastel pinné (image dedans) + scroll par-dessus. */}
                <View style={styles.stage}>
                    {/* Bandeau pastel collé en haut, hauteur animée, image clippée. */}
                    {illustration ? (
                        <Animated.View
                            style={[
                                styles.bgBand,
                                { backgroundColor: illustrationBgColor },
                                bandAnimatedStyle,
                            ]}
                            pointerEvents="none"
                        >
                            <Animated.Image
                                source={illustration}
                                style={[
                                    styles.illustration,
                                    imageAnimatedStyle,
                                ]}
                                resizeMode="contain"
                            />
                        </Animated.View>
                    ) : null}

                    {/* Contenu (TITRE + helper + cartes) qui scrolle PAR-DESSUS le
                        bandeau. Le spacer transparent réserve le bandeau au repos. */}
                    <Animated.ScrollView
                        style={styles.scroll}
                        contentContainerStyle={styles.content}
                        showsVerticalScrollIndicator={false}
                        scrollEventThrottle={16}
                        onScroll={scrollHandler}
                    >
                        {illustration ? (
                            <View
                                style={styles.scrollSpacer}
                                pointerEvents="none"
                            />
                        ) : null}
                        <View style={styles.sheet}>
                            <View style={styles.scrollTitle}>{titleBlock}</View>
                            {answersBlock}
                        </View>
                    </Animated.ScrollView>
                </View>

                {ctaFooter}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    // --- shared ---
    question: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    helper: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.light,
        marginBottom: 0,
    },
    answersList: {
        gap: 12,
        marginTop: 6,
    },
    chipGrid: {
        marginTop: 6,
    },
    footer: {
        paddingHorizontal: 20,
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },

    // --- narrow (mobile) ---
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: {
        flex: 1,
        backgroundColor: theme.colors.white,
    },
    // Progression épinglée tout en haut — fond opaque, au-dessus du bandeau.
    pinnedHeader: {
        backgroundColor: theme.colors.white,
        zIndex: 3,
        paddingHorizontal: 20,
    },
    // Conteneur bandeau + scroll (l'un sur l'autre).
    stage: {
        flex: 1,
        position: "relative",
    },
    // Bandeau pastel pinné en haut — l'illustration vit DEDANS et se fait clipper
    // par le bord supérieur (overflow:hidden) quand elle remonte au scroll. La
    // hauteur est animée (bandAnimatedStyle) ; on garde BAND_MAX par défaut.
    bgBand: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: BAND_MAX,
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        zIndex: 0,
    },
    illustration: {
        width: 156,
        height: 156,
    },
    scroll: {
        flex: 1,
        zIndex: 1,
    },
    // Spacer transparent en tête du scroll = hauteur du bandeau au repos (réserve
    // la place du bandeau ; le contenu remonte par-dessus au scroll).
    scrollSpacer: {
        height: BAND_MAX,
        backgroundColor: "transparent",
    },
    // Feuille de contenu (titre + cartes) — fond opaque, remonte par-dessus le
    // bandeau. Coin supérieur arrondi pour souligner le glissement « sheet ».
    sheet: {
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
        paddingTop: 16,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
    },
    // Le TITRE fait désormais partie du contenu qui défile (plus épinglé).
    scrollTitle: {
        paddingBottom: 10,
    },
    content: {
        paddingBottom: 28,
    },

    // --- wide (desktop split) ---
    wideRoot: {
        flex: 1,
        flexDirection: "row",
        backgroundColor: theme.colors.white,
    },
    widePanel: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
    },
    wideBack: {
        position: "absolute",
        top: 22,
        left: 22,
        width: 36,
        height: 36,
        alignItems: "center",
        justifyContent: "center",
    },
    wideIllustration: {
        width: "60%",
        maxWidth: 380,
        height: "55%",
    },
    wideLogo: {
        position: "absolute",
        bottom: 24,
        left: 24,
        width: 96,
        height: 28,
    },
    wideContentCol: {
        flex: 1,
        backgroundColor: theme.colors.white,
        alignItems: "center",
    },
    // Colonne de contenu capée (WIDE_CONTENT_MAX) ET centrée dans le panneau
    // droit (wideContentCol a alignItems:center) → padding confortable même à
    // 1440 (le titre ne colle plus au bord).
    wideContentInner: {
        flex: 1,
        width: "100%",
        maxWidth: WIDE_CONTENT_MAX,
        paddingHorizontal: 40,
    },
    wideScrollContent: {
        paddingTop: 48,
        paddingBottom: 28,
    },
})

export default FunnelQuestionScreen
